import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import service from '../service';
import type { RegistrationCode } from '../types/registrationCode';
import { inviteCodeQueryKeys, retryInviteCodeMutation } from './useInviteCodes';
import { partnerReferralCodeQueryKeys } from './usePartnerReferralCodes';

export interface DeleteInviteCodeVariables {
  id: string;
}

export function useDeleteInviteCode(
  options?: UseMutationOptions<
    RegistrationCode,
    Error,
    DeleteInviteCodeVariables
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation({
    mutationKey: ['inviteCode', 'delete'],
    mutationFn: async ({ id }: DeleteInviteCodeVariables) =>
      service.deleteRegistrationCode(id),
    retry: retryInviteCodeMutation,
    ...restOptions,
    onSuccess: (data, variables, context, mutation) => {
      // Only lists: the deleted code's usage and signups endpoints 404, so its open pages must not refetch.
      queryClient.invalidateQueries({ queryKey: inviteCodeQueryKeys.lists() });
      // Partner code pages list their linked, non-deleted invite codes.
      queryClient.invalidateQueries({
        queryKey: partnerReferralCodeQueryKeys.all,
      });
      onSuccess?.(data, variables, context, mutation);
    },
  });
}
